import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SkillLevel } from '../../apiClients/workspace/types';
import {
  SKILL_LEVEL_EMOJIS,
  getDefaultIconIdByWeight,
  getEmojiById,
} from '../../lib/userUtils';
import { useSkillLevelsStore } from '../../stores';
import { Button } from './Button';
import { Input } from './Input';

interface SkillLevelEditorProps {
  level: SkillLevel;
  onUpdate: (weight: number, name: string, iconId?: string) => void;
  usedIconIds: string[]; // List of iconIds already used by other levels
  hasIconConflict: boolean; // Whether this level has an icon conflict
  openEmojiPickerWeight: number | null; // Which level's emoji picker is open
  onEmojiPickerToggle: (weight: number | null) => void; // Function to toggle emoji picker
}

const SkillLevelEditor: React.FC<SkillLevelEditorProps> = observer(
  ({
    level,
    onUpdate,
    usedIconIds,
    hasIconConflict,
    openEmojiPickerWeight,
    onEmojiPickerToggle,
  }) => {
    const { t } = useTranslation();
    const [name, setName] = useState(level.name);
    const [selectedIconId, setSelectedIconId] = useState(
      level.settings?.iconId || getDefaultIconIdByWeight(level.weight)
    );

    useEffect(() => {
      setName(level.name);
      setSelectedIconId(
        level.settings?.iconId || getDefaultIconIdByWeight(level.weight)
      );
    }, [level]);

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setName(value);
      onUpdate(
        level.weight,
        value,
        selectedIconId || getDefaultIconIdByWeight(level.weight)
      );
    };

    const handleIconChange = (iconId: string) => {
      setSelectedIconId(iconId);
      onEmojiPickerToggle(null); // Close picker after selection
      onUpdate(level.weight, name, iconId);
    };

    const handleEmojiPickerToggle = () => {
      const newState =
        openEmojiPickerWeight === level.weight ? null : level.weight;
      onEmojiPickerToggle(newState);
    };

    const isEmojiPickerOpen = openEmojiPickerWeight === level.weight;

    const currentEmoji = selectedIconId
      ? getEmojiById(selectedIconId)
      : getEmojiById(getDefaultIconIdByWeight(level.weight));

    // Filter out used icons (except current level's icon)
    const availableEmojis = SKILL_LEVEL_EMOJIS.filter(
      (emoji) => !usedIconIds.includes(emoji.id) || emoji.id === selectedIconId
    );

    return (
      <div
        className={`flex items-center space-x-3 p-3 bg-white border rounded-lg ${
          hasIconConflict ? 'border-red-300 bg-red-50' : 'border-gray-200'
        }`}
      >
        {/* Weight indicator */}
        <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
          <span className="text-xs font-semibold text-blue-600">
            {level.weight}
          </span>
        </div>

        {/* Emoji picker */}
        <div className="relative emoji-picker-container">
          <button
            type="button"
            onClick={handleEmojiPickerToggle}
            className={`flex-shrink-0 w-10 h-10 border rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors ${
              hasIconConflict
                ? 'bg-red-50 border-red-300'
                : 'bg-gray-50 border-gray-300'
            }`}
            title={
              hasIconConflict
                ? t('skillLevels.iconConflictTooltip')
                : t('skillLevels.selectEmoji')
            }
          >
            <span className="text-lg">{currentEmoji}</span>
          </button>

          {isEmojiPickerOpen && (
            <div className="absolute top-12 left-0 z-10 bg-white border border-gray-300 rounded-lg shadow-lg p-2 grid grid-cols-5 gap-1 min-w-max">
              {availableEmojis.map((emoji) => (
                <button
                  key={emoji.id}
                  type="button"
                  onClick={() => handleIconChange(emoji.id)}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors ${
                    selectedIconId === emoji.id
                      ? 'bg-blue-100 border-2 border-blue-500'
                      : ''
                  }`}
                  title={emoji.name}
                >
                  <span className="text-sm">{emoji.emoji}</span>
                </button>
              ))}
              {availableEmojis.length === 0 && (
                <div className="col-span-5 text-center text-xs text-gray-500 p-2">
                  {t('skillLevels.noAvailableEmojis')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Name input */}
        <div className="flex-1">
          <Input
            value={name}
            onChange={handleNameChange}
            placeholder={t('skillLevels.levelNamePlaceholder')}
            maxLength={15}
            className={`w-full text-sm ${hasIconConflict ? 'border-red-300' : ''}`}
          />
          {hasIconConflict && (
            <p className="text-red-600 text-xs mt-1">
              {t('skillLevels.iconConflictMessage')}
            </p>
          )}
        </div>
      </div>
    );
  }
);

interface SkillLevelsEditorProps {
  onSave?: () => void;
  onCancel?: () => void;
}

const SkillLevelsEditor: React.FC<SkillLevelsEditorProps> = observer(
  ({ onSave, onCancel }) => {
    const { t } = useTranslation();
    const skillLevelsStore = useSkillLevelsStore();
    const [levels, setLevels] = useState<SkillLevel[]>([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [openEmojiPickerWeight, setOpenEmojiPickerWeight] = useState<
      number | null
    >(null);

    // Close emoji picker when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element;
        if (!target.closest('.emoji-picker-container')) {
          setOpenEmojiPickerWeight(null);
        }
      };

      if (openEmojiPickerWeight !== null) {
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
          document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [openEmojiPickerWeight]);

    // Initialize levels on component mount
    useEffect(() => {
      const initializeLevels = () => {
        const defaultLevels = skillLevelsStore.createDefaultLevels();
        setLevels(defaultLevels);
      };

      initializeLevels();
    }, [skillLevelsStore.skillLevels, t, skillLevelsStore]);

    // Validate levels for conflicts
    useEffect(() => {
      const errors = skillLevelsStore.validateLevels(levels);
      setValidationErrors(errors);
    }, [levels, skillLevelsStore]);

    const handleLevelUpdate = (
      weight: number,
      name: string,
      iconId?: string
    ) => {
      setLevels((prevLevels) =>
        prevLevels.map((level) =>
          level.weight === weight
            ? {
                ...level,
                name,
                settings: { iconId },
              }
            : level
        )
      );
      setHasChanges(true);
    };

    const hasIconConflicts = (levelWeight: number): boolean => {
      return skillLevelsStore.hasIconConflict(levels, levelWeight);
    };

    const getUsedIconIds = (excludeWeight: number): string[] => {
      return skillLevelsStore.getUsedIconIds(levels, excludeWeight);
    };

    const handleSave = async () => {
      if (validationErrors.length > 0) {
        return;
      }

      const updateRequest = {
        levels: levels.map((level) => ({
          name: level.name.trim(),
          weight: level.weight,
          settings: level.settings,
        })),
      };

      const response = await skillLevelsStore.updateSkillLevels(updateRequest);

      if (response) {
        setHasChanges(false);
        onSave?.();
      }
    };

    const handleCancel = () => {
      // Reset to original state
      const defaultLevels = skillLevelsStore.createDefaultLevels();
      setLevels(defaultLevels);
      setHasChanges(false);
      setValidationErrors([]);
      onCancel?.();
    };

    const canSave =
      hasChanges && validationErrors.length === 0 && !skillLevelsStore.isSaving;

    return (
      <div className="space-y-4">
        {/* Header */}
        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">
            {t('skillLevels.title')}
          </h3>
          <p className="text-xs text-gray-600 mb-3">
            {t('skillLevels.description')}
          </p>

          {/* Validation errors */}
          {validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
              <div className="flex items-start">
                <span className="text-red-600 mr-2">⚠️</span>
                <div>
                  <p className="text-xs font-medium text-red-800 mb-1">
                    {t('skillLevels.validationErrorsTitle')}
                  </p>
                  <ul className="text-xs text-red-700 list-disc list-inside">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Levels list */}
        <div className="space-y-2">
          {levels.map((level) => (
            <SkillLevelEditor
              key={level.weight}
              level={level}
              onUpdate={handleLevelUpdate}
              usedIconIds={getUsedIconIds(level.weight)}
              hasIconConflict={hasIconConflicts(level.weight)}
              openEmojiPickerWeight={openEmojiPickerWeight}
              onEmojiPickerToggle={setOpenEmojiPickerWeight}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-2 pt-3 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={skillLevelsStore.isSaving}
            size="sm"
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!canSave} size="sm">
            {skillLevelsStore.isSaving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </div>
    );
  }
);

export default SkillLevelsEditor;
